import React, { useState, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import i18next from 'i18next';
import { Box, Grid, Typography, Button, IconButton, Paper } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import Logo from '../Common/Logo/Logo';
import auth from '@/apis/auth';
import { errorConfig } from '@/config/i18n';
import { NewTextField } from '../Common/Fields';
import { frontendurl } from '../../index';
import { getPathKey } from '@/common/common';
import { PasswordRequirements } from './PasswordRequirements';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'flex-start',
        [theme.breakpoints.up('md')]: {
            justifyContent: 'center',
            padding: '48px 0',
        },
    },
    appContent: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: '0 auto',
        boxShadow: 'none',
        [theme.breakpoints.up('lg')]: {
            width: 'auto',
        },
        [theme.breakpoints.up('md')]: {
            margin: '0 20px',
        },
        [theme.breakpoints.between('sm', 'md')]: {
            justifyContent: 'unset',
            width: '100%',
            padding: '10px',
            margin: '0 5px',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            margin: '0 5px',
            padding: '10px',
            justifyContent: 'unset',
        },
    },
    cardLayout: {
        width: '352px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '40px 24px',
        borderRadius: '8px',
        margin: '0 auto',
        backgroundColor: '#FFFFFF',
        [theme.breakpoints.up('lg')]: {
            width: '100%',
            maxWidth: '416px',
            padding: '40px 30px',
        },
        [theme.breakpoints.between('sm', 'md')]: {
            width: '100%',
            boxShadow: 'none',
            borderRadius: '0',
            maxWidth: '416px',
            padding: '50px 32px',
        },
        [theme.breakpoints.down('sm')]: {
            boxShadow: 'none',
        },
    },
    formSignin: {
        margin: '0 auto',
    },
    ButtonStyle: {
        height: '50px',
        width: '100%',
        margin: theme.spacing(3, 0, 0, 0),
        backgroundColor: theme.palette.primary.main,
        borderRadius: '4px',
        [theme.breakpoints.up('lg')]: {
            height: '50px',
            fontSize: '16px',
        },
        [theme.breakpoints.between('sm', 'md')]: {
            height: '50px',
            fontSize: '14px',
            margin: theme.spacing(2, 0, 0, 0),
        },
        [theme.breakpoints.down('sm')]: {
            height: '45px',
            fontSize: '12px',
        },
    },
}));

interface ResetPwdState {
    newPassword: string;
    confirmNewPassword: string;
}

const ResetPwd: React.FC = () => {
    const classes = useStyles();
    const [key, setKey] = useState<string | null>(null);
    const [passwordMatches, setPasswordMatches] = useState<boolean>(false);
    const [data, setData] = useState<ResetPwdState>({ newPassword: '', confirmNewPassword: '' });
    const { newPassword, confirmNewPassword } = data;
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
        setKey(getPathKey());
    }, []);

    const handleChange = ({ target: { value, name } }: React.ChangeEvent<HTMLInputElement>) => {
        const newData = { ...data };
        newData[name as keyof ResetPwdState] = value;
        if (newData.newPassword === newData.confirmNewPassword) {
            setPasswordMatches(true);
        } else {
            setPasswordMatches(false);
        }
        setData(newData);
    };

    const send = () => {
        const requestData = {
            new_password: newPassword,
            confirm_new_password: confirmNewPassword,
        };

        const requirementsMet =
            newPassword.length >= 8 &&
            /[a-z]/.test(newPassword) &&
            /[A-Z]/.test(newPassword) &&
            /\d/.test(newPassword) &&
            /[@$!%*?&*\- _+={}\[\]|:;"'<>,.?/~`]/.test(newPassword);

        if (!newPassword || newPassword.length === 0) {
            toastr.error(i18next.t('common:login.password'), i18next.t('common:login.providePass'), errorConfig);
            return;
        }

        if (!requirementsMet) {
            toastr.error(i18next.t('common:login.password'), i18next.t('common:login.pwdResetValidationError'), errorConfig);
            return;
        }

        if (passwordMatches) {
            const pass = JSON.stringify(requestData);

            if (pass) {
                auth
                    .resetPwd(pass, key)
                    .then(() => {
                        toastr.success(i18next.t('common:login.reset'), i18next.t('common:login.pwdResetSuccessfully'));
                        window.location.assign(frontendurl);
                    })
                    .catch((error) => {
                        if (error.response && error.response.status === 400) {
                            toastr.error(i18next.t('common:error'), i18next.t('common:login.pwdResetInvalidLinkError'), errorConfig);
                        } else if (error.response && error.response.status === 422) {
                            toastr.error(i18next.t('common:error'), i18next.t('common:login.pwdResetValidationError'), errorConfig);
                        } else if (error.response && error.response.data && error.response.data.detail) {
                            toastr.error(i18next.t('common:error'), i18next.t('common:login.generalError'), errorConfig);
                        } else {
                            toastr.error(i18next.t('common:error'), i18next.t('common:login.generalError'), errorConfig);
                        }
                    });
            }
        } else {
            toastr.error(i18next.t('common:login.password'), i18next.t('common:login.uneven'), errorConfig);
        }
    };

    return (
        <Box className={classes.root}>
            <Paper className={classes.appContent}>
                <Grid className={classes.cardLayout}>
                    <Grid className="Logo">
                        <Logo w="300" h="50" />
                    </Grid>
                    <form
                        className={classes.formSignin}
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                    >
                        <Grid>
                            <NewTextField
                                onChange={handleChange}
                                value={newPassword}
                                name={'newPassword'}
                                type={showPassword ? 'text' : 'password'}
                                data-cy={'newPassword'}
                                label={i18next.t('common:login.newPassword')}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => setShowPassword((prevState) => !prevState)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    ),
                                }}
                            />
                            <PasswordRequirements password={newPassword} />
                        </Grid>
                        <Grid>
                            <NewTextField
                                onChange={handleChange}
                                value={confirmNewPassword || ''}
                                name={'confirmNewPassword'}
                                type={showConfirmPassword ? 'text' : 'password'}
                                data-cy={'confirmNewPassword'}
                                label={i18next.t('common:login.confirmNewPassword')}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => setShowConfirmPassword((prevState) => !prevState)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid>
                            <Button
                                className={classes.ButtonStyle}
                                data-cy={'submit'}
                                onClick={send}
                                type={'button'}
                                variant={'contained'}
                                color={'primary'}
                            >
                                <Typography variant={'button'}>{i18next.t('common:login.reset')}</Typography>
                            </Button>
                        </Grid>
                    </form>
                </Grid>
            </Paper>
        </Box>
    );
};

export default ResetPwd;
